import { fetch, Method } from '../fetch';
import { toast } from 'react-toastify';

const checkoutRoutes = {
    getBookingItems: (bookingCode) => `/api/v1.1.1/payment/${bookingCode}`,
    validateCheckout: (accessCode) => `/api/v1.1.1/payment/validate/${accessCode}`,
    updatePaymentLink: '/api/v1.1.1/payment/update_payment_link',
    createStripeSession: '/api/v1.1.1/payment/create_stripe_session',
    cancelStripePayment: '/api/v1.1.1/payment/cancel_stripe_payment',
    createEpaycoSession: '/api/v1.1.1/payment/create_epayco_session',
    cancelStatusPaymentLink: () => `/api/v1.1.1/payment/cancel_payment_link`,
    getExperiencesTermsId: (expId, termsId) => `/api/v1.1.1/experience/${expId}/ExperienceTerms/${termsId}`,
};

const defaultErrorMessage = 'Oops, something went wrong. Please try again later.';

export const CheckoutService = {
    getBookingItems: async (bookingCode) => {
        try {
            const { data } = await fetch(checkoutRoutes.getBookingItems(bookingCode), {
                method: Method.GET,
                authenticated: true,
            });

            return data;
        } catch (error) {
            toast.error(defaultErrorMessage, {
                position: 'top-right',
                theme: 'colored',
            });
            throw new Error(error);
        }
    },
    validateCheckout: async (accessCode) => {
        try {
            const { data } = await fetch(checkoutRoutes.validateCheckout(accessCode), {
                method: Method.GET,
                authenticated: true,
            });

            return data;
        } catch (error) {
            toast.error(defaultErrorMessage, {
                position: 'top-right',
                theme: 'colored',
            });
            throw new Error(error);
        }
    },
    updatePaymentLink: async (userData, accessCode) => {
        try {
            const { phoneNumber, phoneNumberCountryCode, firstName, lastName, email, country } = userData;
            const payload = {
                phoneNumber,
                phoneNumberAreaCode: phoneNumberCountryCode,
                firstName,
                lastName,
                email,
                nationality: country,
                accessCode,
            };

            const { data } = await fetch(checkoutRoutes.updatePaymentLink, {
                method: Method.PATCH,
                authenticated: true,
                data: payload,
            });

            if (!data) {
                toast.error('There was an error confirming the reservation', {
                    position: 'top-right',
                    theme: 'colored',
                });
                throw new Error('There was an error confirming the reservation');
            }

            return data;
        } catch (error) {
            toast.error(defaultErrorMessage, {
                position: 'top-right',
                theme: 'colored',
            });
            throw new Error(error);
        }
    },
    createStripeSession: async (bookingCode, userData, accessCode, termsAccepted) => {
        try {
            const { phoneNumber, phoneNumberCountryCode, firstName, lastName, email, country } = userData;

            let payload = {
                bookingCode,
                phoneNumber,
                phoneNumberAreaCode: phoneNumberCountryCode,
                firstName,
                lastName,
                email,
                nationality: country,
                redirectOrigin: window.location.origin,
                accessCode,
                termsAccepted,
            };

            const { data } = await fetch(checkoutRoutes.createStripeSession, {
                method: Method.POST,
                authenticated: true,
                data: payload,
            });

            if (!data?.id) {
                toast.error('No id provided', {
                    position: 'top-right',
                    theme: 'colored',
                });
                throw new Error('No id provided');
            }

            return data;
        } catch (err) {
            toast.error(defaultErrorMessage, {
                position: 'top-right',
                theme: 'colored',
            });
            throw new Error(err);
        }
    },
    cancelStripePayment: async (info) => {
        try {
            const { data } = await fetch(checkoutRoutes.cancelStripePayment, {
                method: Method.DELETE,
                authenticated: true,
                data: info,
            });

            if (!data) {
                throw new Error('Error canceling payments');
            }

            return data;
        } catch (error) {
            toast.error(defaultErrorMessage, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    },
    cancelStatusPaymentLink: async (info) => {
        try {
            const { data } = await fetch(checkoutRoutes.cancelStatusPaymentLink(info), {
                method: Method.PATCH,
                authenticated: true,
                data: info,
            });
            return data;
        } catch (err) {
            throw new Error(err);
        }
    },
    createEpaycoSession: async (bookingCode, userData, accessCode, language) => {
        try {
            const payload = {
                bookingCode,
                userData,
                accessCode,
                redirectOrigin: window.location.origin,
                language,
            };

            const { data } = await fetch(checkoutRoutes.createEpaycoSession, {
                method: Method.POST,
                authenticated: true,
                data: payload,
            });

            if (!data.sessionId) {
                toast.error('No session id provided', {
                    position: 'top-right',
                    theme: 'colored',
                });
                throw new Error('No session id provided');
            }

            return data.sessionId;
        } catch (error) {
            toast.error(defaultErrorMessage, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    },
    getExperiencesTermsId: async (expId, termsId) => {
        try {
            const { data } = await fetch(checkoutRoutes.getExperiencesTermsId(expId, termsId), {
                method: Method.GET,
                authenticated: true,
            });

            if (!data) {
                return false;
            }

            return data;
        } catch (error) {
            throw new Error(error);
        }
    },
};
